<template>
  <Card class="availability-card">
    <v-layout row align-center class="header-section px-4 py-2 full-width">
      <div class="header-title subheading primaryText--text">Availability</div>
      <v-spacer></v-spacer>

      <div class="header-legend d-flex align-center">
        <div class="legend-item mr-4">
          <div class="dot up-dot mr-2"></div>
          <span class="legend-text primaryText--text">Up</span>
        </div>

        <div class="legend-item">
          <div class="dot down-dot mr-2"></div>
          <span class="legend-text primaryText--text">Down</span>
        </div>
      </div>
    </v-layout>

    <v-divider class="custom-divider"></v-divider>

    <v-layout column fill-height class="ma-0">
      <v-layout
        v-if="hasPlantData"
        row
        wrap
        class="ma-0"
        :style="{ flex: plantSectionFlex, minHeight: '170px' }"
      >
        <v-flex xs12 md7 class="pa-3 vertical-divider-right">
          <div class="inline-flex-center mb-3">
            <span class="subheading primaryText--text" style="margin-top: -5px;">{{ isBlockLevel ? 'Block' : 'Plant' }}</span>

            <v-tooltip bottom content-class="ref-device-tooltip">
              <template v-slot:activator="{ on }">
                <v-icon v-on="on" class="ml-1 infoButton" small>info</v-icon>
              </template>

              <div class="pa-2">
                <p class="mb-1 font-weight-bold tooltip-title">
                  {{ $t('Ref. Device(s)') }}
                </p>
                <p class="mb-0 tooltip-text">
                  {{ plantRefDeviceName }}
                </p>
              </div>
            </v-tooltip>
          </div>

          <v-layout row align-center>
            <v-flex xs5>
              <div class="flex-col-center">
                <div class="caption primaryText--text mb-1">Total</div>
                <v-progress-circular
                  :rotate="-90"
                  :size="85"
                  :width="6"
                  :value="widgetData['Plant Availability'] || 0"
                  :color="getDialColor(widgetData['Plant Availability'])"
                  :class="widgetData['Plant Availability'] > 0 ? 'dial-active' : ''"
                >
                  <span class="font-weight-bold value-green">
                    {{ widgetData['Plant Availability'] }}
                  </span>
                </v-progress-circular>
              </div>
            </v-flex>
            <v-flex xs7 class="pl-3">
              <div class="text-xs-left">
                <div class="caption primaryText--text gen-label">
                  {{ $t('Generation hours') }} <span v-if="generationHoursText">{{ generationHoursText }}</span>
                </div>

                <v-progress-linear
                  :value="widgetData['plant_value_generation'] || 0"
                  height="10"
                  :color="widgetData['plant_value_generation'] > 0 ? '#1FCF8E' : '#E9952A'"
                  :background-color="widgetData['plant_value_generation'] > 0 ? '#F64E47' : ($vuetify.dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)')"
                  class="mb-1 rounded-bar"
                ></v-progress-linear>

                <div class="caption font-weight-bold mb-2 value-green">
                  {{ widgetData['plant_value_generation'] > 0 ? widgetData['plant_value_generation'] + '%' : '0%' }}
                </div>

                <div class="caption primaryText--text gen-label">
                  {{ $t('Non generation hours') }}
                </div>

                <v-progress-linear
                  :value="widgetData['plant_value_non_generation'] || 0"
                  height="10"
                  :color="widgetData['plant_value_non_generation'] > 0 ? '#1FCF8E' : '#E9952A'"
                  :background-color="widgetData['plant_value_non_generation'] > 0 ? '#F64E47' : ($vuetify.dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)')"
                  class="mb-1 rounded-bar"
                ></v-progress-linear>

                <div class="caption font-weight-bold value-green">
                  {{ widgetData['plant_value_non_generation'] > 0 ? widgetData['plant_value_non_generation'] + '%' : '0%' }}
                </div>
              </div>
            </v-flex>
          </v-layout>
        </v-flex>

        <v-flex xs12 md5 class="pa-0">
          <div class="plant-list">
            <SimpleList :data="plantMetricsGroup" />
          </div>
        </v-flex>
      </v-layout>

      <v-divider v-if="hasPlantData && hasGridData" class="custom-divider"></v-divider>

      <v-layout v-if="hasGridData" row wrap class="ma-0" style="flex: 1;">
        <v-flex xs12 md7 class="pa-3 vertical-divider-right">
          <div class="inline-flex-center mb-3">
            <span class="subheading primaryText--text" style="margin-top: -3px;">Grid</span>

            <v-tooltip bottom content-class="ref-device-tooltip">
              <template v-slot:activator="{ on }">
                <v-icon v-on="on" class="ml-1 infoButton" small>info</v-icon>
              </template>

              <div class="pa-2">
                <p class="mb-1 font-weight-bold tooltip-title">
                  {{ $t('Ref. Device(s)') }}
                </p>
                <p class="mb-0 tooltip-text">
                  {{ gridRefDeviceName }}
                </p>
              </div>
            </v-tooltip>
          </div>

          <v-layout row align-center>
            <v-flex xs5>
              <div class="flex-col-center">
                <div class="caption primaryText--text mb-1">Total</div>
                <v-progress-circular
                  :rotate="-90"
                  :size="85"
                  :width="6"
                  :value="widgetData['Grid Availability'] || 0"
                  :color="getDialColor(widgetData['Grid Availability'])"
                  :class="widgetData['Grid Availability'] > 0 ? 'dial-active' : ''"
                >
                  <span class="font-weight-bold value-green">
                    {{ widgetData['Grid Availability'] }}
                  </span>
                </v-progress-circular>
              </div>
            </v-flex>
            <v-flex xs7 class="pl-3">
              <div class="text-xs-left">
                <div class="caption primaryText--text gen-label">
                  {{ $t('Generation hours') }} <span v-if="generationHoursText">{{ generationHoursText }}</span>
                </div>

                <v-progress-linear
                  :value="widgetData['grid_value_generation'] || 0"
                  height="10"
                  :color="widgetData['grid_value_generation'] > 0 ? '#1FCF8E' : '#E9952A'"
                  :background-color="widgetData['grid_value_generation'] > 0 ? '#F64E47' : ($vuetify.dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)')"
                  class="mb-1 rounded-bar"
                ></v-progress-linear>

                <div class="caption font-weight-bold mb-2 value-green">
                  {{ widgetData['grid_value_generation'] > 0 ? widgetData['grid_value_generation'] + '%' : '0%' }}
                </div>

                <div class="caption primaryText--text gen-label">
                  {{ $t('Non generation hours') }}
                </div>

                <v-progress-linear
                  :value="widgetData['grid_value_non_generation'] || 0"
                  height="10"
                  :color="widgetData['grid_value_non_generation'] > 0 ? '#1FCF8E' : '#E9952A'"
                  :background-color="widgetData['grid_value_non_generation'] > 0 ? '#F64E47' : ($vuetify.dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)')"
                  class="mb-1 rounded-bar"
                ></v-progress-linear>

                <div class="caption font-weight-bold value-green">
                  {{ widgetData['grid_value_non_generation'] > 0 ? widgetData['grid_value_non_generation'] + '%' : '0%' }}
                </div>
              </div>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex xs12 md5 class="pa-0">
          <div class="py-2 mt-4 grid-list">
            <SimpleList :data="gridMetricsGroup" />
          </div>
        </v-flex>
      </v-layout>
    </v-layout>
  </Card>
</template>

<script>
import SimpleList from "@/components/common/SimpleList.vue";

export default {
  name: "AvailabilityCard",
  components: {
    SimpleList
  },
  props: {
    widgetList: {
      type: Array,
      required: true
    },
    widgetData: {
      type: Object,
      required: true
    },
    metric: {
      type: Array,
      required: true
    },
    generationHoursText: {
      type: String,
      required: true
    },
    isBlockLevel: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    hasPlantData() {
      const val = this.widgetData["Plant Availability"];
      return "Plant Availability" in this.widgetData && val !== undefined && val !== null && val !== "-";
    },
    hasGridData() {
      const val = this.widgetData["Grid Availability"];
      return "Grid Availability" in this.widgetData && val !== undefined && val !== null && val !== "-";
    },
    plantSectionFlex() {
      return this.hasGridData ? '0 0 auto' : '1';
    },
    plantMetricsGroup() {
      return this.metric.filter(
        m =>
          m.name.includes("Start") ||
          m.name.includes("Stop") ||
          m.name.includes("Inst. PA") ||
          m.name.includes("Inst. BA")
      );
    },
    gridMetricsGroup() {
      return this.metric.filter(
        m => m.name.includes("Grid Status") || m.name.includes("Grid Down Time")
      );
    },
    plantRefDeviceName() {
      const plantWidgets = [
        "Plant Availability",
        "Instantaneous Plant Availability"
      ];
      const widget = this.widgetList.find(w => plantWidgets.includes(w.name));
      return widget && widget.refDeviceName ? widget.refDeviceName : "Inverter";
    },
    gridRefDeviceName() {
      const gridWidgets = [
        "Grid Availability",
        "Instant Grid Availability"
      ];
      const widget = this.widgetList.find(w => gridWidgets.includes(w.name));
      return widget && widget.refDeviceName ? widget.refDeviceName : "Meter";
    }
  },
  methods: {
    getDialColor(value) {
      return value > 0 ? "#1FCF8E" : "#E9952A";
    }
  }
};
</script>

<style scoped>
.availability-card {
  height: 100%;
  border-radius: 6px;
  box-shadow: 0px 5px 5px 0px #334079;
  overflow: hidden;
}

.custom-divider {
  background-color: #334079;
  border-color: #334079;
}

.header-section {
  width: 100%;
}

.header-title {
  font-size: 12px;
  white-space: nowrap;
}

.dot {
  min-width: 8px;
  min-height: 8px;
  max-width: 8px;
  max-height: 8px;
  border-radius: 50%;
}

.up-dot {
  background-color: #00e676;
}

.down-dot {
  background-color: #ff5252;
}

.legend-text {
  font-size: 12px;
  opacity: 0.8;
}

.rounded-bar {
  border-radius: 10px;
}

.rounded-bar >>> .v-progress-linear__bar__determinate {
  border-radius: 10px;
}

.vertical-divider-right {
  position: relative;
}

.vertical-divider-right::after {
  content: "";
  position: absolute;
  top: 0px; 
  bottom: 0px;
  right: 0px; 
  width: 1px; 
  background-color: #334079;
}

.v-progress-circular >>> .v-progress-circular__underlay {
  stroke: rgba(255,255,255,0.1);
}

.theme--light .v-progress-circular >>> .v-progress-circular__underlay {
  stroke: rgba(0,0,0,0.1);
}

.dial-active >>> .v-progress-circular__underlay {
  stroke: #f64e47 !important;
}

.plant-list {
  height: 100%;
  padding-top: 16px;
  padding-bottom: 19px;  
}

.plant-list ::v-deep > div {
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  height: 100%;
}

.plant-list ::v-deep .list-item {
  margin-top: 0 !important;
}

.grid-list ::v-deep .list-item {
  padding-top: 16px; 
  padding-bottom: 16px;
}

.infoButton{
  margin-top: -4px;
  cursor: pointer;
}

.gen-label {
  margin-bottom: 1px;
}

.ref-device-label {
  font-size: 14px;
}

.ref-device-name {
  font-size: 12px;
}

.full-width {
  width: 100%;
}

.legend-item {
  display: flex;
  align-items: center;
}

.inline-flex-center {
  display: inline-flex;
  align-items: center;
}

.flex-col-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.value-green {
  color: #00E676;
}

.tooltip-title {
  font-size: 14px;
}

.tooltip-text {
  font-size: 12px;
}

.theme--light .ref-device-tooltip {
  background-color: #E7F1E8 !important;
  color: #000000 !important;
}
.theme--dark .ref-device-tooltip {
  background-color: var(--v-primary-base) !important; 
  color: #ffffff !important;
}

.v-progress-circular {
  font-size: 14px;
}

.subheading {
  font-size: 14px;
  letter-spacing: 1px;
}

.caption {
  font-size: 11px;
  line-height: 1.2;
}
</style>
